import React, { FC, useState, useEffect } from 'react';
import { Box, Divider, Grid, TextField, Typography, Button, MenuItem } from '@mui/material';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';
import MaskedTextInputFormik from 'components/MaskedTextInputFormik';
import CustomPhoneField from 'components/CustomPhoneField';
import { useSnackbar } from 'notistack';
// import { formatCurrency } from 'utils/banking';
import { EDeclaredCompanyRevenue } from 'common/banking.interface';
import { IPrePartner } from 'common/create-banking-account.dto';
import AddressForm from './AddressForm';
import PartnerPreRegister from './PartnerPreRegister';

const revenueOptions = [
  {
    value: EDeclaredCompanyRevenue.UP_TO_81000,
    label: 'Até R$ 81.000',
  },
  {
    value: EDeclaredCompanyRevenue.FROM_81001_TO_360000,
    label: 'R$ 81.001 - R$ 360.000',
  },
  {
    value: EDeclaredCompanyRevenue.FROM_360001_TO_4800000,
    label: 'R$ 360.001 - R$ 4.800.000',
  },
  {
    value: EDeclaredCompanyRevenue.FROM_4800001_TO_78000000,
    label: 'R$ 4.800.001 - R$ 78.000.000',
  },
  {
    value: EDeclaredCompanyRevenue.ABOVE_78000000,
    label: 'Acima de R$ 78.000.000',
  },
];

type CompanyValues = {
  cnpj: string;
  tradingName: string;
  businessName: string;
  email: string;
  phoneNumber: string;
  revenue: string;
  addressPostalCode: string;
  addressState: string;
  addressCity: string;
  addressNeighborhood: string;
  addressStreet: string;
  addressNumber: string;
  addressComplement: string;
};

type Props = {
  initialValues: CompanyValues;
  partners: IPrePartner[];
  setPartners: React.Dispatch<React.SetStateAction<IPrePartner[]>>;
  onNext: (values: CompanyValues) => void;
};

const validationSchemaStep1 = Yup.object({
  cnpj: Yup.string().required('Obrigatório'),
  tradingName: Yup.string().required('Obrigatório'),
  businessName: Yup.string().required('Obrigatório'),
  email: Yup.string().email('Email inválido').required('Obrigatório'),
  phoneNumber: Yup.string().required('Obrigatório'),
  revenue: Yup.string().required('Obrigatório'),
  addressPostalCode: Yup.string().required('Obrigatório'),
  addressState: Yup.string().required('Obrigatório'),
  addressCity: Yup.string().required('Obrigatório'),
  addressNeighborhood: Yup.string().required('Obrigatório'),
  addressStreet: Yup.string().required('Obrigatório'),
  addressNumber: Yup.string().required('Obrigatório'),
});

const Step1CompanyForm: FC<Props> = ({ initialValues, partners, setPartners, onNext }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [partnerConditions, setPartnerConditions] = useState({
    hasPartners: false,
    hasMajorPartner: false,
    hasResponsible: false,
    hasAnyError: false,
    isUnder100Percent: false,
  });

  useEffect(() => {
    const realPartners = partners.filter((p) => !p.fromRepresentativeForm);

    const total = partners.reduce((sum, p) => sum + Number(p.participation.replace('%', '')), 0);

    setPartnerConditions({
      hasPartners: realPartners.length > 0,
      hasMajorPartner: partners.some((p) => parseFloat(p.participation) >= 25),
      hasResponsible: partners.some((p) => p.responsible),
      hasAnyError: partners.some((p) => p.isError),
      isUnder100Percent: total <= 100,
    });
  }, [partners]);

  const getFieldError = (fieldName: keyof CompanyValues, touched: any, errors: any) => touched[fieldName] && Boolean(errors[fieldName]);
  const getFieldHelperText = (fieldName: keyof CompanyValues, touched: any, errors: any) =>
    touched[fieldName] && errors[fieldName] ? String(errors[fieldName]) : '';

  const handleNext = (values: CompanyValues) => {
    if (!partnerConditions.hasPartners) {
      enqueueSnackbar('Adicione pelo menos um sócio.', { variant: 'error' });
      return;
    }

    if (!partnerConditions.hasMajorPartner) {
      enqueueSnackbar('Pelo menos um sócio deve ter participação igual ou superior a 25%.', { variant: 'error' });
      return;
    }

    if (!partnerConditions.hasResponsible) {
      enqueueSnackbar('Selecione o responsável pela abertura da conta.', { variant: 'error' });
      return;
    }

    if (partnerConditions.hasAnyError) {
      enqueueSnackbar('Revise o representante legal informado.', { variant: 'error' });
      return;
    }

    if (!partnerConditions.isUnder100Percent) {
      enqueueSnackbar('A participação total dos sócios não pode exceder 100%.', { variant: 'error' });
      return;
    }

    onNext(values);
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchemaStep1} onSubmit={handleNext} enableReinitialize>
      {({ values, errors, touched, setFieldValue }) => (
        <FormikForm>
          <Grid item xs={12} sm={6} mb={1} sx={{ mt: 2 }}>
            <MaskedTextInputFormik
              variant="filled"
              mode="cnpj"
              name="cnpj"
              label="Qual é o CNPJ da empresa"
              value={values.cnpj}
              onChange={(e: any) => setFieldValue('cnpj', e.target.value)}
              error={touched.cnpj && Boolean(errors.cnpj)}
              helperText={touched.cnpj && errors.cnpj ? String(errors.cnpj) : ''}
              required
              fullWidth
              InputProps={{ readOnly: true }}
              sx={{ pointerEvents: 'none' }}
            />
          </Grid>

          <Grid item xs={12} sm={6} mb={1} sx={{ mt: 2 }}>
            <TextField
              variant="filled"
              name="tradingName"
              label="Nome fantasia"
              value={values.tradingName}
              onChange={(e) => setFieldValue('tradingName', e.target.value)}
              error={touched.tradingName && Boolean(errors.tradingName)}
              helperText={touched.tradingName && errors.tradingName ? String(errors.tradingName) : ''}
              required
              fullWidth
              InputProps={{ readOnly: true }}
              sx={{ pointerEvents: 'none' }}
            />
          </Grid>

          <Grid item xs={12} sm={6} mb={1} sx={{ mt: 2 }}>
            <TextField
              variant="filled"
              name="businessName"
              label="Razão social"
              value={values.businessName}
              onChange={(e) => setFieldValue('businessName', e.target.value)}
              error={touched.businessName && Boolean(errors.businessName)}
              helperText={touched.businessName && errors.businessName ? String(errors.businessName) : ''}
              fullWidth
              required
              InputProps={{ readOnly: true }}
              sx={{ pointerEvents: 'none' }}
            />
          </Grid>

          <Grid item xs={12} sm={6} mb={1} sx={{ mt: 2 }}>
            <TextField
              variant="filled"
              name="email"
              label="E-mail da empresa"
              required
              value={values.email}
              placeholder="email@email.com.br"
              onChange={(e) => setFieldValue('email', e.target.value)}
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email ? String(errors.email) : ''}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={{ pointerEvents: 'none' }}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 4 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomPhoneField
              name="phoneNumber"
              label="Telefone da empresa"
              value={values.phoneNumber}
              placeholder="(DDD) 00000-0000"
              onChange={(e: any) => setFieldValue('phoneNumber', e.target.value)}
              error={touched.phoneNumber && Boolean(errors.phoneNumber)}
              helperText={touched.phoneNumber && errors.phoneNumber ? String(errors.phoneNumber) : ''}
              fullWidth
              required
            />
          </Grid>

          <Grid item xs={12} sm={6} mb={1} sx={{ mt: 2 }}>
            <TextField
              select
              variant="standard"
              name="revenue"
              label="Faixa de faturamento anual da empresa"
              value={values.revenue}
              onChange={(e) => setFieldValue('revenue', e.target.value)}
              error={touched.revenue && Boolean(errors.revenue)}
              helperText={touched.revenue && errors.revenue ? String(errors.revenue) : ''}
              required
              fullWidth
            >
              {revenueOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Box sx={{ mt: '4vh', mb: '4vh' }}>
            <Typography sx={{ fontWeight: 600, fontSize: '16px', color: '#383736' }}>Endereço comercial</Typography>
            <AddressForm
              values={values as any}
              setFieldValue={setFieldValue}
              errors={errors}
              touched={touched}
              getFieldError={getFieldError}
              getFieldHelperText={getFieldHelperText}
              enqueueSnackbar={enqueueSnackbar}
            />
          </Box>

          <PartnerPreRegister partners={partners} setPartners={setPartners} />

          <Box display="flex" justifyContent="flex-end" sx={{ mt: '4vh', mb: '4vh' }}>
            <Button type="submit" variant="contained" color="primary">
              Avançar
            </Button>
          </Box>
        </FormikForm>
      )}
    </Formik>
  );
};

export default Step1CompanyForm;
